/**
 * 評価ページの会話ログで発言者を示す小さな丸アイコン。
 * 患者・歯科医はアバターの顔画像、学習者はプロフィール画像（本人が見ているときだけ）。
 */
import { useState } from "react";
import { UserRound } from "lucide-react";
import { ProfileImage } from "./student/ProfileImage";

const ICON_SIZE = 32;
const COLORS = {
  stripDot: "#E8DFC4",
  stripInk: "#4B4738",
  cyan: "#0E7490",
  cyanSoft: "#ECFEFF",
};

function isFemale(gender: string | null | undefined): boolean {
  const value = String(gender ?? "").trim().toLowerCase();
  return value === "女性" || value === "female" || value === "f";
}

/** 患者アバターの静止画（server の AvatarAssetCatalog と同じ配置） */
export function patientStillImageUrl(
  gender: string | null | undefined,
  ageRange: string | null | undefined,
): string | null {
  const age =
    ageRange === "20-34" ? "20_34"
      : ageRange === "35-49" ? "35_49"
      : ageRange === "50+" || ageRange === "50-64" || ageRange === "65+" ? "50_plus"
      : null;
  if (!gender || !age) return null;
  return `/videos/assets/patients/${isFemale(gender) ? "female" : "male"}_${age}/base/avatar.png`;
}

/** 歯科医アバターの静止画。性別が分からないときは面接中と同じく女性医師 */
export function dentistStillImageUrl(gender: string | null | undefined): string {
  const profile = gender === "男性" || String(gender ?? "").toLowerCase() === "male" ? "male_default" : "female_default";
  return `/videos/assets/dentists/${profile}/base/avatar.jpg`;
}

const circleStyle = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  borderRadius: 999,
  overflow: "hidden",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
} as const;

export function AvatarFaceIcon({ src, label }: { src: string | null; label: string }) {
  // 面接中に相手（患者→歯科医）が替わっても前の失敗を引きずらないよう、失敗した URL で覚える
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (!src || failedSrc === src) {
    return (
      <span
        aria-label={label}
        role="img"
        style={{ ...circleStyle, background: COLORS.stripDot, color: COLORS.stripInk, fontSize: 13, fontWeight: 800 }}
      >
        {label.slice(0, 1)}
      </span>
    );
  }
  return (
    <span style={{ ...circleStyle, background: COLORS.stripDot }}>
      <img
        src={src}
        alt={label}
        onError={() => setFailedSrc(src)}
        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
      />
    </span>
  );
}

export function LearnerIcon({ showProfileImage }: { showProfileImage: boolean }) {
  if (showProfileImage) return <ProfileImage size={ICON_SIZE} />;
  return (
    <span aria-label="学習者" role="img" style={{ ...circleStyle, background: COLORS.cyanSoft, color: COLORS.cyan }}>
      <UserRound size={Math.round(ICON_SIZE / 1.7)} />
    </span>
  );
}
